// Loads the sample pack built by tools/sounds/build_pack.py (public/sounds) and decodes
// just the sounds a piece needs. The engine plays these through the same buses, sidechain
// and reverb as its oscillator patches, which remain the fallback when a sample is missing.

const BASE = `${process.env.PUBLIC_URL || ''}/sounds`;

let manifestPromise = null;
const buffers = new Map();        // file -> Promise<AudioBuffer>
let offsetPromise = null;         // MP3 decoder start offset in seconds

export function loadManifest() {
  if (!manifestPromise) {
    manifestPromise = fetch(`${BASE}/manifest.json`).then((r) => {
      if (!r.ok) throw new Error(`manifest ${r.status}`);
      return r.json();
    });
    manifestPromise.catch(() => { manifestPromise = null; });
  }
  return manifestPromise;
}

function decode(ctx, file) {
  if (!buffers.has(file)) {
    const p = fetch(/^https?:/.test(file) ? file : `${BASE}/${file}`)
      .then((r) => { if (!r.ok) throw new Error(`${file} ${r.status}`); return r.arrayBuffer(); })
      .then((ab) => ctx.decodeAudioData(ab));
    p.catch(() => buffers.delete(file));
    buffers.set(file, p);
  }
  return buffers.get(file);
}

// MP3 decoders prepend a few hundred samples of silence; measure it once with a click
// encoded at a known time so sample starts and loop points can be corrected
function decoderOffset(ctx, manifest) {
  if (!offsetPromise) {
    offsetPromise = decode(ctx, 'calibration.mp3').then((buf) => {
      const d = buf.getChannelData(0);
      let peak = 0;
      let at = 0;
      for (let i = 0; i < d.length; i++) if (Math.abs(d[i]) > peak) { peak = Math.abs(d[i]); at = i; }
      const off = at / buf.sampleRate - manifest.calibrationClickAt;
      return Math.abs(off) < 0.0003 ? 0 : off;
    }).catch(() => 0);
  }
  return offsetPromise;
}

// Resolve everything a piece needs. Returns null when the pack is unavailable.
export async function preload(ctx, patchNames, kit) {
  let manifest;
  try {
    manifest = await loadManifest();
  } catch (e) {
    return null;
  }
  const pack = { patches: {}, kit: {}, offset: await decoderOffset(ctx, manifest) };
  const jobs = [];
  for (const name of new Set(patchNames)) {
    const entry = manifest.patches[name];
    if (!entry) continue;
    const samples = [];
    pack.patches[name] = { kind: entry.kind, loop: entry.loop, samples };
    for (const s of entry.samples) {
      jobs.push(decode(ctx, s.file).then((buffer) => samples.push({ note: s.note, buffer })).catch(() => {}));
    }
  }
  const kitEntry = manifest.kits[kit];
  if (kitEntry) {
    for (const [gm, s] of Object.entries(kitEntry)) {
      jobs.push(decode(ctx, s.file).then((buffer) => { pack.kit[gm] = buffer; }).catch(() => {}));
    }
  }
  await Promise.all(jobs);
  for (const p of Object.values(pack.patches)) p.samples.sort((a, b) => a.note - b.note);
  return pack;
}

export function nearestSample(samples, midi) {
  let best = samples[0];
  for (const s of samples) if (Math.abs(s.note - midi) < Math.abs(best.note - midi)) best = s;
  return best;
}
